import { ChannelKind, Status } from '@api/generated/models';
import { AnalyticService } from '@application/analytic/analytic.service';
import { BreakPoints, Icon, IconDictionary, useBreakpoint } from '@cellula/react';
import { ActionCard } from '@components/action_card/action_card.component';
import { useAnalyticContext } from '@components/analytic_link/analytic_link.provider';
import { ErrorBoundary } from '@components/error_boundary.component';
import { ExternalSvg } from '@components/external_svg.component';
import { type AccessFlow, FlexibleActionCardModel } from '@services/flexible_component/mod';
import { useService } from '@visiba/instantiation';
import { Fragment } from 'react';
import { Routeable } from './_flexible_identifier_routeable.component';

interface Props {
	component: FlexibleActionCardModel;
	__TEMP__on_OPW_WebContentButton_proxy_invoke?: (flexibleIdentifierData: {
		url: string;
		openUrlWith: string;
		requiresAuthentication: boolean;
	}) => void;
	accessFlow?: AccessFlow;
}

function getIcon(icon: NonNullable<FlexibleActionCardModel['icon']>): JSX.Element {
	switch (icon.type) {
		case 'local':
			return (
				<ErrorBoundary fallback={<Fragment />}>
					<Icon size='small' type={icon.origin as keyof IconDictionary} />
				</ErrorBoundary>
			);
		case 'svgUrl':
			return <ExternalSvg imgSrc={icon.origin} size={32} />;
		case 'pngUrl':
			return <img src={icon.origin} height={32} alt='' />;
		default: {
			const shouldNeverHappen: never = icon.type;

			return shouldNeverHappen;
		}
	}
}

function getButtonType(component: FlexibleActionCardModel): 'booking' | 'messaging' | 'drop-in' | 'unknown' {
	if (/\/booking/i.test(component.url)) {
		return 'booking';
	}
	if (/\/message/i.test(component.url)) {
		return 'messaging';
	}
	if (/\/dropin/i.test(component.url)) {
		return 'drop-in';
	}
	return 'unknown';
}

export function FlexibleActionCard({
	component,
	__TEMP__on_OPW_WebContentButton_proxy_invoke,
	accessFlow,
	...rest
}: Props): JSX.Element {
	const iconElement = component.icon != null ? getIcon(component.icon) : undefined;
	const breakpoint = useBreakpoint();
	const analyticService = useService(AnalyticService);
	const analyticContext = useAnalyticContext();
	const buttonType = getButtonType(component);

	const channel = component.channel;
	const isClosedMessaging =
		channel === ChannelKind.Messaging &&
		accessFlow?.messaging.status !== Status.Unavailable &&
		accessFlow?.messaging.status === Status.Closed;
	const requiresAuthentication = isClosedMessaging ? false : component.requiresAuthentication;
	const url = isClosedMessaging ? getClosedMessagingUrl(component.url) : component.url;

	function analyticQuirk_handleButtonClick(): void {
		//                                         If the url was not parsed through "hostnameIsWithinSameDomain"
		//                                         (within flexible_component.service), it'll not be an absolute.
		//                                         And if it's not absolute it should count as an external button.
		if (component.openUrlWith !== 'SameTab' || component.url.includes('http')) {
			analyticService.trackEvent(analyticContext, `Clicked: External button`);

			return;
		}

		analyticService.trackEvent(analyticContext, `Clicked: button ${buttonType}`);
	}

	return (
		<Routeable
			__TEMP__on_OPW_WebContentButton_proxy_invoke={__TEMP__on_OPW_WebContentButton_proxy_invoke}
			url={url}
			requiresAuthentication={requiresAuthentication}
			openWith={component.openUrlWith}
			sourceButtonId={component.id}
			{...rest}
		>
			<ActionCard
				data-testid='FlexibleActionCard'
				variant={breakpoint === BreakPoints.Small ? 'slim' : 'chunky'}
				title={component.title}
				icon={iconElement}
				subTitle={component.subtitle}
				isStatic
				onClick={analyticQuirk_handleButtonClick}
				accessFlow={accessFlow}
				buttonType={channel}
			/>
		</Routeable>
	);
}

function getClosedMessagingUrl(url: string): string {
	return url.replace(/\/message(?:\/[^?#]*)?/, '/message/closed');
}
